/** Tolerance used by {@link approximately} and by the engine's default equality checks. */
export const EPSILON = 1e-6;

const RADIANS_PER_DEGREE = Math.PI / 180;
const DEGREES_PER_RADIAN = 180 / Math.PI;

/** Restricts a value to the inclusive range between `min` and `max`. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Restricts a value to the inclusive range zero to one. */
export function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

/** Linearly interpolates from `start` to `end`, with `t` clamped to zero to one. */
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * clamp01(t);
}

/** Returns the position of `value` between `start` and `end` as a fraction from zero to one. */
export function inverseLerp(start: number, end: number, value: number): number {
  if (start === end) {
    return 0;
  }
  return clamp01((value - start) / (end - start));
}

/** Converts degrees to radians, the unit every rotation in the engine uses. */
export function degreesToRadians(degrees: number): number {
  return degrees * RADIANS_PER_DEGREE;
}

/** Converts radians to degrees, for display and for authoring content in friendlier units. */
export function radiansToDegrees(radians: number): number {
  return radians * DEGREES_PER_RADIAN;
}

/** Reports whether two numbers are equal to within `tolerance`, guarding against float drift. */
export function approximately(a: number, b: number, tolerance: number = EPSILON): boolean {
  return Math.abs(a - b) <= tolerance;
}

/** Returns `value` when it is a finite number, and `fallback` when it is NaN or an infinity. */
export function toFinite(value: number, fallback = 0): number {
  return Number.isFinite(value) ? value : fallback;
}

/**
 * X of the point `(x, y)` rotated counter-clockwise about the origin.
 *
 * Takes the cosine and sine rather than the angle, so a caller rotating a point pays for one pair of
 * trig calls instead of two, and pairs with {@link rotatedY} to fill whatever shape it is writing into
 * without allocating a vector to carry the result.
 */
export function rotatedX(x: number, y: number, cos: number, sin: number): number {
  return x * cos - y * sin;
}

/** Y of the point `(x, y)` rotated counter-clockwise about the origin. See {@link rotatedX}. */
export function rotatedY(x: number, y: number, cos: number, sin: number): number {
  return x * sin + y * cos;
}
